import { IonSegment, IonSegmentButton, IonicSlides } from '@ionic/react';
import '@ionic/react/css/ionic-swiper.css';
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMeasure } from 'react-use';
import { Swiper as SwiperInterface } from 'swiper';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/zoom';
import {
  Autoplay,
  Keyboard,
  Pagination,
  Scrollbar,
  Zoom,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import './tabs.css';

type TabButtonLayout =
  | 'icon-bottom'
  | 'icon-end'
  | 'icon-hide'
  | 'icon-start'
  | 'icon-top'
  | 'label-hide';

interface TopTabsProps {
  tabs: TopTab[];
  initialTab?: string;
  layout?: TabButtonLayout;
  onTabChange?: (id: string) => void;
  configure?: {
    unmountOnChange?: boolean;
  };
}

export interface TopTab {
  id: string;
  label: ReactElement | string;
  component: ReactElement;
  layout?: TabButtonLayout;
}

const TopTabs = (props: TopTabsProps): ReactElement => {
  const { tabs, layout, initialTab } = props;
  const initialSlide = tabs.findIndex((t) => t.id === initialTab);

  const swiperRef = useRef<SwiperInterface>();
  const [topTab, setTopTab] = useState<string | undefined>(
    initialTab ?? tabs[0].id
  );

  const { onTabChange, configure: configureOptions } = props;

  const onTabChangeInternal = useCallback(
    (tab: TopTab) => {
      const speed = topTab === tab.id ? 0 : 250;
      setTopTab(tab.id);
      if (onTabChange) {
        onTabChange(tab.id);
      }
      const index = tabs.findIndex((t) => t.id === tab.id);

      if (swiperRef.current) {
        swiperRef.current.slideTo(index, speed);
        swiperRef.current.wrapperEl.scrollTop = 0;
      }
    },
    [onTabChange, tabs, topTab]
  );

  const onChangeSize = () => {
    swiperRef.current?.updateAutoHeight(0);
  };

  const [ref, { height: segmentHeight }] = useMeasure<HTMLIonSegmentElement>();
  swiperRef.current?.el.style.setProperty(
    '--slider-header-height',
    `${segmentHeight}px`
  );
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <IonSegment
        ref={ref}
        className="top-tabs"
        mode="md"
        scrollable={true}
        swipeGesture={false}
        value={topTab}
      >
        {generateTopTabs(tabs, onTabChangeInternal, layout)}
      </IonSegment>
      <Swiper
        key={tabs.map((t) => t.id).join('_')}
        className="slides-wrapper-custom"
        onSwiper={(ref) => (swiperRef.current = ref)}
        initialSlide={initialSlide}
        modules={[Autoplay, Keyboard, Pagination, Scrollbar, Zoom, IonicSlides]}
        allowTouchMove={false}
        slidesPerView={1}
        autoHeight={true}
        pagination={false}
      >
        {tabs.map((tab, index) => {
          const isSelected = topTab === tab.id;
          let tabComponent: ReactElement | null = tab.component;

          if (configureOptions?.unmountOnChange && !isSelected) {
            tabComponent = null;
          }

          return (
            <SwiperSlide key={index} style={{ height: '100%' }}>
              <SlideComponent onChangeSize={onChangeSize}>
                <>{tabComponent}</>
              </SlideComponent>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

const generateTopTabs = (
  items: TopTab[],
  onTabChange: (tab: TopTab) => void,
  layout?: TabButtonLayout
): ReactElement[] => {
  return items.map((tab, index) => {
    return (
      <Tab key={index} tab={tab} onTabChange={onTabChange} layout={layout} />
    );
  });
};

interface TabProps {
  tab: TopTab;
  onTabChange: (tab: TopTab) => void;
  layout?: TabButtonLayout;
}

const Tab = (props: TabProps): ReactElement => {
  const { tab, onTabChange, layout } = props;
  const buttonRef = useRef<HTMLIonSegmentButtonElement>(null);
  return (
    <IonSegmentButton
      ref={buttonRef}
      value={tab.id}
      layout={layout}
      onClick={() => {
        onTabChange(tab);
        buttonRef.current?.scrollIntoView({ block: 'nearest', inline: 'end' });
      }}
    >
      {tab.label || tab.id}
    </IonSegmentButton>
  );
};

interface SlideComponentProps {
  onChangeSize: () => void;
  children: ReactNode;
}

const SlideComponent = (props: SlideComponentProps): ReactElement => {
  const { onChangeSize, children } = props;
  const priorHeightRef = useRef<number>();
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    if (priorHeightRef.current !== height) {
      priorHeightRef.current = height;
      onChangeSize();
    }
  }, [height, onChangeSize]);

  return (
    <div
      ref={ref}
      className={'machine-slide max-content-width slide-wrapper-custom'}
    >
      {children}
    </div>
  );
};

export { TopTabs };
